import type { ReactNode } from "react";

import { GLYPH_REGISTRY } from "./editorial-glyph-registry";

// Re-exported so consumers have one import site for the pair. A re-export
// rather than a declaration here: a component file that also declares a
// constant loses Fast Refresh for the whole module.
export { GLYPH_REGISTRY } from "./editorial-glyph-registry";

import {
    VOCABULARY_BY_SEMANTIC,
    type EditorialGlyphUsage,
    type EditorialSemantic,
    type EditorialToneToken,
} from "./utils/editorial-policy";

/**
 * Tone token to colour. The manifest says WHICH tone a semantic carries; this
 * says what that tone looks like, which is a different fact and the only one
 * that belongs to the view.
 *
 * `warning` and `negative` take the table chips' `STATUS_STYLES` hues, so a
 * glyph in prose and a status chip in a table speak one colour language.
 * `positive` deliberately does not: the chip is indigo because Quoted / Bound /
 * Active are in-flight states rather than approvals, while a ✅ in prose IS the
 * approval, so it stays green. Same token name, two different claims.
 *
 * `-700` rather than the more obvious `-600`: amber-600 on white measures
 * ~3.4:1, which clears the 3:1 non-text bar by too little for a mark that is
 * now the ONLY carrier of its meaning — the emoji it replaced is gone. `-700`
 * measures ~5:1 and reads as ink rather than as a highlight, which is also the
 * quieter answer.
 */
const TONE_CLASS = {
    neutral: "text-muted-foreground",
    positive: "text-green-700 dark:text-green-300",
    warning: "text-amber-700 dark:text-amber-300",
    negative: "text-rose-700 dark:text-rose-300",
} satisfies Record<EditorialToneToken, string>;

export interface EditorialGlyphProps {
    readonly semantic: EditorialSemantic;
    /** Selects the functional label; the glyph looks the same either way. */
    readonly usage: EditorialGlyphUsage;
    /** The authored source this glyph replaced, rendered if it cannot be. */
    readonly children?: ReactNode;
}

/**
 * One unit of meaning, set at text size.
 *
 * Sized in `em` rather than the fixed `h-4 w-4` used elsewhere in this app: a
 * glyph that lives inside prose has to scale with the prose, and a 16px mark in
 * a 14px list item is visibly a pasted icon. The `-0.125em` shift is the reason
 * it reads as a character instead: a 1em box centres on the LINE box, while
 * text centres nearer the x-height, so the unshifted glyph rides high in every
 * dense list and table cell.
 *
 * `leading-none` is load-bearing, not tidying. An inline-block establishes its
 * own line box, and that box carries a STRUT sized by the inherited
 * `line-height`; with the icon shifted down, the wrapper's bottom can fall below
 * the surrounding text's descender and grow the containing line. Collapsing the
 * strut to 1em keeps the wrapper (~1.125em) comfortably inside a normal prose
 * line, so the glyph costs no vertical space. `tool-fallback.tsx` pairs the two
 * for the same reason.
 *
 * The wrapper carries the name and the icon is silent, rather than labelling
 * the `svg` directly as `BooleanIconCell` does — one node announces, one node
 * draws. Colour comes through `currentColor`, so forced-colours mode keeps the
 * mark visible after the palette is discarded.
 */
export const EditorialGlyph = ({
    semantic,
    usage,
    children,
}: EditorialGlyphProps) => {
    const Icon = GLYPH_REGISTRY[semantic];
    const entry = VOCABULARY_BY_SEMANTIC.get(semantic);

    // Fail open. An unrecognized semantic means this layer does not understand
    // the source, and the reader is better served by what the author wrote than
    // by a mark that guesses.
    if (!Icon || !entry) return <>{children}</>;

    const label =
        entry.accessibleLabels[usage] ?? entry.accessibleLabels.inline;
    if (label === undefined) return <>{children}</>;

    return (
        <span
            role="img"
            aria-label={label}
            className={`inline-block leading-none ${TONE_CLASS[entry.tone]}`}
        >
            <Icon
                className="inline h-[1em] w-[1em] align-[-0.125em]"
                aria-hidden="true"
                focusable="false"
            />
        </span>
    );
};
